/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useAssetImages } from 'sarsaparilla';

import { HeroImageGalleryTall } from './HeroImageGalleryTall';
import { PARTNERS } from './GatewayV1Hero.utils';

interface GatewayV1HeroProps {
    /** The id of the campground/permit/ticket facility/gateway/POI that the
     * api will use to fetch the images.
     * Do not use the images prop if using this prop. */
    assetId: string;

    /** The id of the campsite that the api will use to fetch the images
     * Do not use the images prop if using this prop.
     */
    campsiteId?: string;

    /** The tour id the api will use to fetch the images
     * Do not use the images prop if using this prop.
     */
    tourId?: string;

    /** Shows the thumbnail strip at the bottom of the open gallery */
    hasThumbnailStrip?: boolean;

    /** Title of the page/hero image */
    title: string;

    /**  Managing government agency */
    partnerAgency?: keyof typeof PARTNERS;

    /** The state where this inventory is located */
    locationState: string;

    /** Total number of campgrounds */
    totalCampgrounds: number;

    /** Total number of activities */
    totalActivities: number;

    /** Total number of POI */
    totalPOI: number;

    /** On Click function for Campgrounds */
    onCampgroundsClick?: () => void;

    /** On Click function for Activities */
    onActivitiesClick?: () => void;

    /** On Click function for POI */
    onPOIClick?: () => void;

    /** Inventory list renders as buttons */
    inventoryListButtons?: boolean;

    /** Url of official website */
    officialUrl?: string;

    /** Additional className added to the outer container */
    className?: string;

    /** Favorite entity id */
    favoriteEntityId?: string;

    /** Favorite entity type */
    favoriteEntityType?: string;

    /** Boolean value on whether to ignore the inventory count */
    isInventoryCountIgnored?: boolean;
}

export function GatewayV1Hero({
    assetId,
    campsiteId,
    tourId,
    hasThumbnailStrip = true,
    title,
    partnerAgency,
    locationState,
    totalCampgrounds,
    totalActivities,
    totalPOI,
    onCampgroundsClick,
    onActivitiesClick,
    onPOIClick,
    inventoryListButtons,
    officialUrl,
    className,
    favoriteEntityId,
    favoriteEntityType,
    isInventoryCountIgnored = false,
}: GatewayV1HeroProps) {
    const { heroImage, galleryImages, status } = useAssetImages({
        assetId,
        campsiteId,
        tourId,
    });

    return (
        <HeroImageGalleryTall
            heroImage={heroImage}
            galleryImages={galleryImages}
            status={status}
            hasThumbnailStrip={hasThumbnailStrip}
            title={title}
            partnerAgency={partnerAgency}
            locationState={locationState}
            totalCampgrounds={totalCampgrounds}
            totalActivities={totalActivities}
            onCampgroundsClick={onCampgroundsClick}
            onActivitiesClick={onActivitiesClick}
            onPOIClick={onPOIClick}
            totalPOI={totalPOI}
            inventoryListButtons={inventoryListButtons}
            officialUrl={officialUrl}
            className={className}
            favoriteEntityId={favoriteEntityId}
            favoriteEntityType={favoriteEntityType}
            isInventoryCountIgnored={isInventoryCountIgnored}
        />
    );
}

// cSpell:ignore NOAA, USACE, USFS, USFWS
